import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ExternalLink, Calendar, Loader2 } from "lucide-react";
import ShareStrip from "@/components/common/ShareStrip";

// Public Google Calendar Appointment Schedule
// - Short URL (used for "Open full page" external link)
// - Full URL with ?gv=true (used for iframe embed — short URL blocks framing)
const GOOGLE_BOOK_URL = "https://calendar.app.google/kqqnFx17fWZX7msW8";
const GOOGLE_EMBED_URL =
  "https://calendar.google.com/calendar/appointments/schedules/AcZssZ2tBigNtoN6LlAxdP3FgTbZJmTzBLcQE1AgwKP_tFDeX9Rx1rcG356wkHEn4EUbawxsBwYm6QF0?gv=true";

const DiscoveryCallModal = ({ open, onClose }) => {
  const [iframeLoaded, setIframeLoaded] = useState(false);

  // Lock body scroll while open
  useEffect(() => {
    if (open) {
      const previous = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previous;
      };
    }
  }, [open]);

  // Reset iframe loading state on open
  useEffect(() => {
    if (open) setIframeLoaded(false);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[410] overflow-y-auto overscroll-contain flex items-start md:items-center justify-center p-4 md:p-8"
          data-testid="discovery-call-modal"
        >
          <button
            type="button"
            aria-label="Close discovery call modal"
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm"
            data-testid="discovery-call-modal-backdrop"
          />

          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative w-full max-w-5xl my-4 md:my-0 rounded-3xl bg-[#0a0510] border border-white/10 overflow-hidden shadow-[0_40px_120px_-20px_rgba(0,240,255,0.35)]"
          >
            {/* Ambient glow */}
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#00f0ff]/15 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#ff0055]/12 blur-3xl pointer-events-none" />

            {/* Header */}
            <div className="relative flex items-center justify-between gap-4 px-5 md:px-7 py-4 border-b border-white/10 bg-[#11091a]/70 backdrop-blur">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-full bg-[#00f0ff]/10 border border-[#00f0ff]/30 flex items-center justify-center flex-shrink-0">
                  <Calendar size={18} className="text-[#00f0ff]" />
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#00f0ff]">
                    Free · 30 min · Discovery Call
                  </div>
                  <div className="font-display font-black text-lg md:text-xl tracking-tight truncate">
                    Book a Discovery Call
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={GOOGLE_BOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="discovery-open-in-google"
                  className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#a395c3] hover:text-white transition-colors px-3 py-2 rounded-full border border-white/10 hover:border-white/30"
                >
                  Open in Google
                  <ExternalLink size={12} />
                </a>
                
                <ShareStrip campaign="discovery_call" title="Book a discovery call — SB Media Studio" text="Free 15-min call to figure out the right package for you." className="mr-1" />
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="discovery-call-close"
                  aria-label="Close"
                  className="w-10 h-10 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 flex items-center justify-center transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Iframe area */}
            <div className="relative bg-white h-[600px] md:h-[720px]">
              {!iframeLoaded && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-[#05050a] text-[#a395c3]"
                  data-testid="discovery-call-loading"
                >
                  <Loader2 size={28} className="animate-spin text-[#00f0ff]" />
                  <div className="text-xs uppercase tracking-[0.28em]">
                    Loading Google Calendar…
                  </div>
                  <a
                    href={GOOGLE_BOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 text-xs text-[#00f0ff] hover:underline"
                  >
                    Trouble loading? Open in a new tab →
                  </a>
                </div>
              )}
              <iframe
                title="Book a discovery call on Google Calendar"
                src={GOOGLE_EMBED_URL}
                onLoad={() => setIframeLoaded(true)}
                className="w-full h-full border-0"
                data-testid="discovery-call-iframe"
                allow="clipboard-write"
              />
            </div>

            {/* Footer helper */}
            <div className="relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-5 md:px-7 py-3 border-t border-white/10 bg-[#11091a]/70 backdrop-blur">
              <div className="text-[11px] text-[#a395c3]">
                Pick any open slot — we&apos;ll follow up by phone or Google Meet (your choice at booking). For studio sessions or Spotlight guest spots, use the Book Session or Guest form instead.
              </div>
              <a
                href={GOOGLE_BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="discovery-footer-open-google"
                className="sm:hidden inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#00f0ff] hover:text-white transition-colors"
              >
                Open in Google
                <ExternalLink size={11} />
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default DiscoveryCallModal;
export { GOOGLE_BOOK_URL };
